import React, { Component } from 'react';
import { Route, Switch, Redirect, withRouter } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import flatten from 'flat';

import HeadConfigurer from './components/HeadConfigurer/HeadConfigurer';
import LoadingIndicator from './components/LoadingIndicator/LoadingIndicator';
import MissingTenantId from './containers/MissingTenantId/MissingTenantId';
import Registration from './containers/Registration/Registration';
import Activation from './containers/Activation/Activation';

const DEFAULT_LOCALE = 'en';

/**
 * Start the app:
 * - load default and selected translation and merge them
 * - check tenant id exists
 */
class App extends Component {
  constructor(props) {
    super(props);
    const urlParams = new URLSearchParams(props.location.search);

    this.state = {
      locale: this.getAppLocale(urlParams),
      tenant: this.getTenantId(urlParams)
    };
  }

  componentDidMount() {
    const locales = [DEFAULT_LOCALE];
    if (DEFAULT_LOCALE !== this.state.locale.language) {
      locales.push(this.state.locale.language);
    }

    sessionStorage.setItem('accessibleView', 'false');
    Promise.all(locales.map(locale => this.loadTranslation(locale))).then(responses => {
      const localeData = responses
        .map(translations => flatten(translations))
        .reduce((result, translations, i, [defaultTranslations]) => {
          result[locales[i]] = { ...defaultTranslations, ...translations };
          return result;
        }, {});

      this.setState({
        localeData
      });
    });
  }

  getAppLocale = urlParams => {
    if (urlParams.has('lang') && urlParams.get('lang').indexOf('-') > -1) {
      const [language, country] = urlParams.get('lang').split('-');
      return {
        language,
        country
      };
    } else if (urlParams.has('lang') && urlParams.get('lang').length >= 2) {
      const language = urlParams.get('lang').substr(0, 2);
      return {
        language: language,
        country: language.toUpperCase()
      };
    } else {
      const [language, country] = document.documentElement.lang.split('-');
      return {
        language,
        country
      };
    }
  };

  getTenantId = urlParams => {
    return urlParams.get('tenant_id') || process.env.REACT_APP_DEFAULT_TENANT_ID;
  };

  loadTranslation = locale => {
    return new Promise(resolve => {
      fetch(`${process.env.PUBLIC_URL}/locales/${locale}/translation.json`)
        .then(res => {
          if (res.status !== 200) {
            throw new Error('Bad response from server');
          }
          return res.json();
        })
        .then(localeData => {
          resolve(localeData);
        })
        .catch(() => {
          resolve({});
        });
    });
  };

  render() {
    const { locale, tenant, localeData } = this.state;

    return localeData ? (
      <IntlProvider locale={locale.language} messages={localeData[locale.language]}>
        <HeadConfigurer />
        <Switch>
          <Route path="/activation/:tenantId/:uuid" component={Activation} />
          <Route path="/">
            {tenant ? <Registration locale={locale} tenant={tenant} /> : <MissingTenantId />}
          </Route>
          <Redirect to="/" />
        </Switch>
      </IntlProvider>
    ) : (
      <LoadingIndicator />
    );
  }
}

export default withRouter(App);
